import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalKey = 'privacy' | 'terms' | null;
const content: Record<Exclude<ModalKey, null>, {
  title: string;
  body: React.ReactNode;
}> = {
  privacy: {
    title: 'Política de Privacidad',
    body: <>
        <p className="text-sm text-charcoal-light" data-id="element-37">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
        </p>

        <h3 data-id="element-38">1. Introducción</h3>
        <p data-id="element-39">
          En <strong data-id="element-40">The Inflammatix Reset</strong> respetamos tu privacidad y
          nos comprometemos a proteger los datos personales que compartes con
          nosotros. Esta política explica qué información recopilamos, cómo la
          usamos y los derechos que tienes sobre ella.
        </p>

        <h3 data-id="element-41">2. Información que recopilamos</h3>
        <p data-id="element-42">
          Recopilamos información que tú nos proporcionas directamente al
          adquirir el producto, así como datos técnicos generados
          automáticamente al navegar nuestro sitio, incluyendo:
        </p>
        <ul data-id="element-43">
          <li data-id="element-44">
            Nombre y dirección de correo electrónico al realizar una compra.
          </li>
          <li data-id="element-45">
            Información de pago procesada de forma segura por nuestro proveedor
            de pagos (no almacenamos datos de tarjetas).
          </li>
          <li data-id="element-46">
            Datos de navegación: dirección IP, tipo de dispositivo, navegador y
            páginas visitadas.
          </li>
          <li data-id="element-47">
            Cookies y tecnologías similares para mejorar la experiencia del
            usuario.
          </li>
        </ul>

        <h3 data-id="element-48">3. Cómo usamos tu información</h3>
        <p data-id="element-49">Utilizamos los datos recopilados exclusivamente para:</p>
        <ul data-id="element-50">
          <li data-id="element-51">Entregarte el producto digital que has comprado.</li>
          <li data-id="element-52">Procesar pagos y emitir comprobantes.</li>
          <li data-id="element-53">
            Enviarte actualizaciones del protocolo y contenido educativo
            relacionado (puedes darte de baja en cualquier momento).
          </li>
          <li data-id="element-54">
            Mejorar nuestros productos, contenido y experiencia de usuario.
          </li>
          <li data-id="element-55">Cumplir con obligaciones legales y fiscales.</li>
        </ul>

        <h3 data-id="element-56">4. Compartir información con terceros</h3>
        <p data-id="element-57">
          No vendemos ni alquilamos tu información personal. Solo compartimos
          datos con proveedores estrictamente necesarios para operar el servicio
          (procesador de pagos, plataforma de envío de correos, hosting), bajo
          acuerdos de confidencialidad y protección de datos.
        </p>

        <h3 data-id="element-58">5. Seguridad de los datos</h3>
        <p data-id="element-59">
          Implementamos medidas técnicas y organizativas razonables para
          proteger tu información contra acceso no autorizado, alteración,
          divulgación o destrucción. Sin embargo, ningún sistema en internet es
          100% seguro.
        </p>

        <h3 data-id="element-60">6. Cookies</h3>
        <p data-id="element-61">
          Usamos cookies para recordar tus preferencias, analizar el tráfico y
          optimizar el rendimiento del sitio. Puedes deshabilitarlas desde la
          configuración de tu navegador, aunque algunas funciones podrían dejar
          de estar disponibles.
        </p>

        <h3 data-id="element-62">7. Tus derechos</h3>
        <p data-id="element-63">
          Tienes derecho a acceder, rectificar, eliminar o limitar el
          tratamiento de tus datos personales, así como a oponerte a su uso o
          solicitar su portabilidad. Puedes ejercer estos derechos a través de
          los canales disponibles en tu cuenta de usuario.
        </p>

        <h3 data-id="element-64">8. Retención de datos</h3>
        <p data-id="element-65">
          Conservamos tu información únicamente durante el tiempo necesario para
          cumplir las finalidades descritas en esta política o lo exigido por la
          ley aplicable.
        </p>

        <h3 data-id="element-66">9. Cambios a esta política</h3>
        <p data-id="element-67">
          Podemos actualizar esta Política de Privacidad ocasionalmente.
          Cualquier cambio significativo será notificado en esta misma página
          con su fecha de actualización correspondiente.
        </p>
      </>
  },
  terms: {
    title: 'Términos y Condiciones',
    body: <>
        <p className="text-sm text-charcoal-light" data-id="element-68">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
        </p>

        <h3 data-id="element-69">1. Aceptación de los términos</h3>
        <p data-id="element-70">
          Al acceder, navegar o comprar el producto digital{' '}
          <strong data-id="element-71">"The Inflammatix Reset"</strong>, aceptas quedar vinculado por
          estos Términos y Condiciones. Si no estás de acuerdo, te pedimos no
          utilizar el sitio ni adquirir el producto.
        </p>

        <h3 data-id="element-72">2. Descripción del producto</h3>
        <p data-id="element-73">
          "The Inflammatix Reset" es un producto digital de contenido educativo
          e informativo enfocado en hábitos alimenticios y de estilo de vida. El
          acceso es inmediato tras confirmar el pago y se entrega en formato
          digital descargable.
        </p>

        <h3 data-id="element-74">3. Aviso médico</h3>
        <p data-id="element-75">
          El contenido de este producto tiene fines exclusivamente informativos
          y educativos.{' '}
          <strong data-id="element-76">
            No constituye consejo médico, diagnóstico ni tratamiento
          </strong>
          . Antes de iniciar cualquier cambio nutricional o de estilo de vida,
          especialmente si tienes una condición médica preexistente, estás
          embarazada, en lactancia o tomando medicamentos, consulta a un
          profesional de la salud calificado.
        </p>

        <h3 data-id="element-77">4. Licencia de uso</h3>
        <p data-id="element-78">
          Al adquirir el producto, recibes una licencia personal, no exclusiva e
          intransferible para usar el contenido únicamente con fines personales.
          Queda expresamente prohibido:
        </p>
        <ul data-id="element-79">
          <li data-id="element-80">
            Revender, redistribuir, copiar o publicar el contenido total o
            parcialmente.
          </li>
          <li data-id="element-81">Compartir tu acceso con terceros.</li>
          <li data-id="element-82">
            Usar el material con fines comerciales o de capacitación a terceros
            sin autorización por escrito.
          </li>
        </ul>

        <h3 data-id="element-83">5. Política de reembolso</h3>
        <p data-id="element-84">
          Debido a la naturaleza digital del producto y a que el acceso se
          entrega de forma inmediata, todas las ventas son finales y no admiten
          reembolso, salvo en los casos exigidos expresamente por la legislación
          aplicable.
        </p>

        <h3 data-id="element-85">6. Resultados</h3>
        <p data-id="element-86">
          Los resultados pueden variar de una persona a otra y dependen de
          múltiples factores individuales (genética, hábitos previos, adherencia
          al protocolo, condiciones médicas, etc.). No garantizamos resultados
          específicos ni que el protocolo sea adecuado para todas las personas.
        </p>

        <h3 data-id="element-87">7. Propiedad intelectual</h3>
        <p data-id="element-88">
          Todo el contenido del sitio y del producto (textos, imágenes, marcas,
          logotipos, diseño) está protegido por derechos de autor y propiedad
          intelectual. Su uso no autorizado puede dar lugar a acciones legales.
        </p>

        <h3 data-id="element-89">8. Limitación de responsabilidad</h3>
        <p data-id="element-90">
          En la máxima medida permitida por la ley, no seremos responsables por
          daños directos, indirectos, incidentales o consecuentes derivados del
          uso del producto o del sitio. El uso del protocolo es bajo tu propia
          responsabilidad.
        </p>

        <h3 data-id="element-91">9. Modificaciones</h3>
        <p data-id="element-92">
          Nos reservamos el derecho de actualizar estos Términos en cualquier
          momento. La versión vigente será siempre la publicada en esta página
          junto con su fecha de actualización.
        </p>

        <h3 data-id="element-93">10. Ley aplicable</h3>
        <p data-id="element-94">
          Estos Términos se rigen por las leyes aplicables a la jurisdicción del
          operador del sitio. Cualquier controversia se resolverá conforme a
          dichas leyes.
        </p>
      </>
  }
};
export function Footer() {
  const [modal, setModal] = useState<ModalKey>(null);
  // Lock body scroll when modal open + close on Escape
  useEffect(() => {
    if (!modal) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [modal]);
  return <>
      <footer className="border-t border-charcoal/10 bg-cream-dark py-12" data-id="element-95">
        <div className="mx-auto max-w-4xl px-6 text-center" data-id="element-96">
          <p className="font-serif text-lg text-charcoal" data-id="element-97">
            The Inflammatix Reset
          </p>

          <p className="mx-auto mt-4 max-w-2xl text-xs leading-relaxed text-charcoal-light" data-id="element-98">
            Este contenido tiene fines puramente informativos y educativos. No
            constituye consejo médico, diagnóstico ni tratamiento. Consulta
            siempre a un profesional de la salud antes de iniciar cualquier
            protocolo nutricional, especialmente si tienes una condición
            preexistente.
          </p>

          <nav className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs font-medium uppercase tracking-[0.15em] text-charcoal-light" data-id="element-99">
            <button onClick={() => setModal('privacy')} className="transition-colors hover:text-amber" data-id="element-100">
              Política de privacidad
            </button>
            <span className="text-charcoal/20" data-id="element-101">·</span>
            <button onClick={() => setModal('terms')} className="transition-colors hover:text-amber" data-id="element-102">
              Términos y condiciones
            </button>
          </nav>

          <p className="mt-8 text-xs text-charcoal-light" data-id="element-103">
            © {new Date().getFullYear()} The Inflammatix Reset. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-104">
        {modal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-end justify-center bg-charcoal/60 p-0 backdrop-blur-sm sm:items-center sm:p-6" onClick={() => setModal(null)} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-105">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-cream shadow-premium sm:rounded-2xl" data-id="element-106">
              {/* Header */}
              <div className="flex items-start justify-between gap-4 border-b border-charcoal/10 bg-cream-dark px-6 py-5 sm:px-8" data-id="element-107">
                <h2 id="legal-modal-title" className="font-serif text-2xl leading-tight text-charcoal md:text-3xl" data-id="element-108">
                  {content[modal].title}
                </h2>
                <button onClick={() => setModal(null)} className="-mr-2 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-charcoal-light transition-colors hover:bg-charcoal/5 hover:text-charcoal" aria-label="Cerrar" data-id="element-109">
                  <XIcon className="h-5 w-5" data-id="element-110" />
                </button>
              </div>

              {/* Body */}
              <div className="legal-prose flex-1 overflow-y-auto px-6 py-6 sm:px-8 sm:py-8" data-id="element-111">
                {content[modal].body}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}